import axios from "axios";
import { APP_PATH } from "../api/backendPath";
import { handleAccountDeactivated } from "../utils/accountDeactivated";

/**
 * SessionService - Manages automatic token refresh and session monitoring
 *
 * This service works for both Doctor (role_id: 4) and Pharmacy (role_id: 5) users
 * since both use the same:
 * - Refresh token endpoint: /api/v1/consultation/refresh-token
 * - Session storage keys: usr_1a2b3c (access_token) and usr_4d5e6f (refresh_token)
 *
 * The service automatically:
 * - Validates session every 10 minutes
 * - Refreshes tokens before expiry
 * - Monitors 2-day session duration
 * - Handles session expiry gracefully
 */
class SessionService {
  constructor() {
    this.validationInterval = null;
    this.accountCheckInterval = null;
    this.refreshTimer = null;
    this.VALIDATION_INTERVAL = 10 * 60 * 1000; // Check every 10 minutes
    this.ACCOUNT_CHECK_INTERVAL = 30 * 1000; // Check account is still active every 30s
    this.SESSION_WARNING_TIME = 30 * 60 * 1000; // Warn 30 minutes before session expiry
    this.DOCTOR_SESSION_DURATION = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds (applies to both Doctor and Pharmacy)
    this.REFRESH_BEFORE_EXPIRY = 12 * 1000; // Refresh 12 seconds before token expires
  }

  /**
   * Start session validation monitoring
   */
  startSessionMonitoring() {
    // Clear any existing interval
    this.stopSessionMonitoring();
    // Start validation interval
    this.validationInterval = setInterval(() => {
      this.validateSession();
    }, this.VALIDATION_INTERVAL);

    // Poll the account status so a session sitting idle is logged out promptly
    // when an admin deactivates the account (the gateway returns 403
    // ACCOUNT_DEACTIVATED for every authenticated request once blocked).
    this.accountCheckInterval = setInterval(() => {
      this.checkAccountActive();
    }, this.ACCOUNT_CHECK_INTERVAL);

    // Start proactive token refresh timer
    this.scheduleTokenRefresh();

    // Don't validate immediately on page load - give the user time to interact
    // The interval will handle validation after VALIDATION_INTERVAL
  }

  /**
   * Stop session validation monitoring
   */
  stopSessionMonitoring() {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
      console.log("⏹️ Stopped session monitoring");
    }
    if (this.accountCheckInterval) {
      clearInterval(this.accountCheckInterval);
      this.accountCheckInterval = null;
    }
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
      console.log("⏹️ Stopped token refresh timer");
    }
  }

  /**
   * Lightweight check that the account is still active. Hits an authenticated
   * endpoint (behind the gateway AuthGuard); when the account has been
   * deactivated the gateway responds 403 ACCOUNT_DEACTIVATED and we show the
   * block modal. Any other error is ignored here (handled by the normal flows).
   */
  async checkAccountActive() {
    const access_token = sessionStorage.getItem("usr_1a2b3c");
    if (!access_token) return;
    try {
      await axios.get(process.env.REACT_APP_API_URL + APP_PATH.doctorProfile, {
        headers: { Authorization: `Bearer ${access_token}` },
      });
    } catch (error) {
      handleAccountDeactivated(error);
    }
  }

  /**
   * Validate current session
   */
  async validateSession() {
    const access_token = sessionStorage.getItem("usr_1a2b3c");
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");

    // Skip validation if not authenticated
    if (!access_token || !refresh_token) {
      console.log("⏭️ Skipping session validation - no tokens found");
      return;
    }

    // First check if the session has actually expired (2 days)
    if (this.isSessionExpired()) {
      console.log("⏰ Session has expired (2 days limit reached)");
      this.handleSessionExpiry("Session expired - 2 days limit reached");
      return;
    }

    try {
      console.log("🔍 Validating session...");

      // Use the validate-token endpoint
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.validateToken,
        { token: access_token },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Check if session is still valid
      if (!response?.data?.data?.valid) {
        console.log("⚠️ Token is invalid, attempting refresh...");
        // Token is invalid, try to refresh it
        await this.attemptTokenRefresh(refresh_token);
      } else {
        console.log("✅ Session is valid");
      }
    } catch (error) {
      console.warn("⚠️ Session validation failed:", error);

      // Only try to refresh if it's a 401 error (token expired)
      if (error?.response?.status === 401) {
        await this.attemptTokenRefresh(refresh_token);
      } else if (
        error.message?.includes("Network Error") ||
        error.message?.includes("timeout")
      ) {
        // Don't handle network errors as session expiry
        console.warn(
          "🌐 Network error during session validation, skipping expiry handling",
        );
      } else {
        // For other errors, don't try to refresh - just log and continue
        console.warn(
          "❌ Session validation failed with non-401 error, not attempting refresh",
        );
      }
    }
  }

  /**
   * Decode JWT token to get expiration time
   */
  getTokenExpiration(token) {
    if (!token) return null;
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      const payload = JSON.parse(jsonPayload);
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      console.warn("Failed to decode token:", error);
      return null;
    }
  }

  /**
   * Schedule proactive token refresh before expiration
   */
  scheduleTokenRefresh() {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const access_token = sessionStorage.getItem("usr_1a2b3c");
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");

    if (!access_token || !refresh_token) {
      return;
    }

    const expirationTime = this.getTokenExpiration(access_token);
    if (!expirationTime) {
      console.warn(
        "⚠️ Could not determine token expiration, will validate on next interval",
      );
      return;
    }

    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;
    const refreshTime = timeUntilExpiry - this.REFRESH_BEFORE_EXPIRY;

    // If token expires very soon or already expired, refresh immediately
    if (refreshTime <= 0 || timeUntilExpiry <= 0) {
      console.log(
        "🔄 Token expired or expiring soon, refreshing immediately...",
      );
      this.attemptTokenRefresh(refresh_token);
      // Schedule next refresh after getting new token
      setTimeout(() => this.scheduleTokenRefresh(), 1000);
      return;
    }

    // If refresh time is too far in the future (more than 14 minutes),
    // set a reasonable maximum delay (13 minutes) and reschedule
    const maxDelay = 13 * 60 * 1000; // 13 minutes max
    const delay = Math.min(refreshTime, maxDelay);

    this.refreshTimer = setTimeout(() => {
      const currentRefreshToken = sessionStorage.getItem("usr_4d5e6f");
      if (currentRefreshToken) {
        this.attemptTokenRefresh(currentRefreshToken)
          .then(() => {
            // Reschedule for the new token
            this.scheduleTokenRefresh();
          })
          .catch((error) => {
            console.error("❌ Proactive refresh failed:", error);
            // Retry after a delay if refresh fails
            setTimeout(() => this.scheduleTokenRefresh(), 60000); // Retry after 1 minute
          });
      }
    }, delay);
  }

  /**
   * Attempt to refresh the access token
   */
  async attemptTokenRefresh(refreshToken) {
    if (!refreshToken) {
      this.handleSessionExpiry("No refresh token available");
      return;
    }

    try {
      console.log("🔄 Attempting to refresh access token...");

      const access_token = sessionStorage.getItem("usr_1a2b3c");

      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.refreshToken,
        { refresh_token: refreshToken },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${access_token}`,
            "User-Refresh-Token": `${refreshToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response?.data?.data) {
        // Update the tokens in sessionStorage
        sessionStorage.setItem("usr_1a2b3c", response.data.data.access_token);
        sessionStorage.setItem("usr_4d5e6f", response.data.data.refresh_token);
        console.log("✅ Token refreshed successfully");

        // Reschedule refresh for the new token
        this.scheduleTokenRefresh();

        return true;
      } else {
        console.warn("⚠️ Token refresh response invalid");
        this.handleSessionExpiry("Invalid refresh response");
        return false;
      }
    } catch (error) {
      console.warn("❌ Token refresh failed:", error);

      // Check for specific error codes
      if (
        error?.response?.data?.error_code === "AUTH_010" ||
        error?.response?.data?.message?.includes(
          "Invalid or expired refresh token",
        ) ||
        error?.response?.status === 400
      ) {
        console.error("🚨 Refresh token is invalid or expired");
        this.handleSessionExpiry("Refresh token expired - session expired");
        return false;
      }

      // Only treat as session expiry if it's a 401 error (refresh token expired)
      if (error?.response?.status === 401) {
        this.handleSessionExpiry("Refresh token expired - session expired");
        return false;
      } else {
        // For other errors (network, server errors), don't treat as session expiry
        console.warn(
          "⚠️ Token refresh failed with non-401 error, not treating as session expiry",
        );
        // Retry after a short delay
        setTimeout(() => {
          const currentRefreshToken = sessionStorage.getItem("usr_4d5e6f");
          if (currentRefreshToken) {
            this.attemptTokenRefresh(currentRefreshToken);
          }
        }, 5000); // Retry after 5 seconds
        return false;
      }
    }
  }

  /**
   * Handle session expiry
   */
  handleSessionExpiry(reason) {
    console.log("🚨 Session expired:", reason);

    // Stop monitoring
    this.stopSessionMonitoring();

    // Clear session and redirect
    this.clearSessionAndRedirect();
  }

  /**
   * Clear session and redirect to login
   */
  clearSessionAndRedirect() {
    // Stop monitoring
    this.stopSessionMonitoring();

    // Clear session start time
    try {
      sessionStorage.removeItem("sessionStartTime");
    } catch (error) {
      console.warn("Failed to clear session start time:", error);
    }

    // Clear all session storage
    sessionStorage.clear();

    // Redirect to login page
    window.location.href = "/";
  }

  /**
   * Check if session is about to expire based on session creation time
   * This checks the actual session expiry (2 days) not token expiry (15 minutes)
   */
  isSessionAboutToExpire() {
    const sessionStartTime = this.getSessionStartTime();
    if (!sessionStartTime) return false;

    const now = Date.now();
    const timeSinceStart = now - sessionStartTime;
    const timeUntilExpiry = this.DOCTOR_SESSION_DURATION - timeSinceStart;

    return timeUntilExpiry <= this.SESSION_WARNING_TIME;
  }

  /**
   * Get session start time from sessionStorage
   */
  getSessionStartTime() {
    try {
      const stored = sessionStorage.getItem("sessionStartTime");
      if (stored) {
        return parseInt(stored, 10);
      }
      return null;
    } catch (error) {
      console.warn("Failed to get session start time:", error);
      return null;
    }
  }

  /**
   * Check if session has actually expired (2 days)
   */
  isSessionExpired() {
    const sessionStartTime = this.getSessionStartTime();
    if (!sessionStartTime) {
      // If no session start time, consider it expired
      return true;
    }

    const now = Date.now();
    const timeSinceStart = now - sessionStartTime;

    return timeSinceStart >= this.DOCTOR_SESSION_DURATION;
  }

  /**
   * Set session start time (call this on login)
   */
  setSessionStartTime() {
    try {
      sessionStorage.setItem("sessionStartTime", Date.now().toString());
      console.log("✅ Session start time set");
    } catch (error) {
      console.warn("Failed to set session start time:", error);
    }
  }
}

// Export singleton instance
export const sessionService = new SessionService();
